import * as React from "react";

interface Props {
	onStrokeChange: (hasStroke: boolean) => void;
}

/**
 * Small dependency-free signature pad with high-DPI canvas output.
 */
export default class SignaturePad extends React.PureComponent<Props, {}> {
	private canvas: HTMLCanvasElement;
	private drawing: boolean = false;
	private hasStroke: boolean = false;

	/**
	 * Bound once and reused as the ref, rather than a fresh inline arrow function per render: an inline
	 * ref changes identity on every render, which makes React detach and reattach it (null, then the
	 * element again) even though the underlying <canvas> node never changed. That detach can land between
	 * a stroke's pointerdown and its pointermove, leaving a dot with no way to continue or clear.
	 */
	private setCanvasRef = (element: HTMLCanvasElement) => this.canvas = element;

	componentDidMount() {
		this.prepareCanvas();
		this.canvas.addEventListener("pointerdown", this.startPointer);
		this.canvas.addEventListener("pointermove", this.movePointer);
		this.canvas.addEventListener("pointerup", this.endPointer);
		this.canvas.addEventListener("pointerleave", this.endPointer);
	}

	componentWillUnmount() {
		if (!this.canvas) return;
		this.canvas.removeEventListener("pointerdown", this.startPointer);
		this.canvas.removeEventListener("pointermove", this.movePointer);
		this.canvas.removeEventListener("pointerup", this.endPointer);
		this.canvas.removeEventListener("pointerleave", this.endPointer);
	}

	render() {
		return (
			<div className={`c-taxi-claim__signature-pad${this.hasStroke ? " is-signed" : ""}`}>
				<button type="button" className="c-taxi-claim__signature-clear" onClick={() => this.clear()}>
					Clear
				</button>
				<canvas
					ref={this.setCanvasRef}
					className="c-taxi-claim__signature-canvas"
					aria-label="Signature pad — draw your signature"/>
				<div className="c-taxi-claim__signature-line"/>
				<span className="c-taxi-claim__signature-hint">Sign here with your finger or mouse</span>
			</div>
		);
	}

	public clear() {
		const ctx = this.canvasContext();
		if (ctx && this.canvas) {
			const ratio = window.devicePixelRatio || 1;
			ctx.clearRect(0, 0, this.canvas.width / ratio, this.canvas.height / ratio);
		}
		this.drawing = false;
		this.hasStroke = false;
		this.props.onStrokeChange(false);
		this.forceUpdate();
	}

	public toBlob(): Promise<Blob> {
		return new Promise<Blob>((resolve, reject) => {
			if (!this.canvas || !this.hasStroke) {
				reject(new Error("Please add your signature first."));
				return;
			}
			this.canvas.toBlob(blob => {
				if (blob) resolve(blob);
				else reject(new Error("Your browser could not prepare the signature."));
			}, "image/png");
		});
	}

	private prepareCanvas() {
		if (!this.canvas) return;
		const ratio = window.devicePixelRatio || 1;
		const width = this.canvas.parentElement ? this.canvas.parentElement.clientWidth : 320;
		const height = 180;
		this.canvas.width = Math.round(width * ratio);
		this.canvas.height = Math.round(height * ratio);
		this.canvas.style.width = `${width}px`;
		this.canvas.style.height = `${height}px`;
		const ctx = this.canvasContext();
		if (ctx) {
			ctx.scale(ratio, ratio);
			ctx.strokeStyle = "#1f2b20";
			ctx.lineWidth = 2.4;
			ctx.lineCap = "round";
			ctx.lineJoin = "round";
		}
	}

	/**
	 * Deliberately not named (or stored in a field named) "context": every React.Component instance has
	 * its own this.context for the legacy Context API, defaulting to {} when the component declares no
	 * contextTypes. React reassigns that own property around each update, silently overwriting a same-
	 * named field holding the canvas's 2D context - the draw stayed wired to whatever this.context.lineTo
	 * happened to resolve to at the time, which is how a stroke ends after a single point with "lineTo is
	 * not a function": this.context had quietly become {} again. Fetching fresh under a different name
	 * sidesteps the collision entirely; it's also cheap, since a canvas hands back the same context object
	 * on every getContext("2d") call once it has one.
	 */
	private canvasContext(): CanvasRenderingContext2D | null {
		return this.canvas ? this.canvas.getContext("2d") : null;
	}

	private startPointer = (event: PointerEvent) => {
		const ctx = this.canvasContext();
		if (!ctx || !this.canvas) return;
		event.preventDefault();
		this.canvas.setPointerCapture(event.pointerId);
		const point = this.position(event.clientX, event.clientY);
		this.drawing = true;
		ctx.beginPath();
		ctx.moveTo(point.x, point.y);
		ctx.lineTo(point.x + 0.01, point.y + 0.01);
		ctx.stroke();
		if (!this.hasStroke) {
			this.hasStroke = true;
			this.props.onStrokeChange(true);
			this.forceUpdate();
		}
	};

	private movePointer = (event: PointerEvent) => {
		const ctx = this.canvasContext();
		if (!this.drawing || !ctx) return;
		event.preventDefault();
		const point = this.position(event.clientX, event.clientY);
		ctx.lineTo(point.x, point.y);
		ctx.stroke();
	};

	private endPointer = () => {
		this.drawing = false;
	};

	private position(clientX: number, clientY: number): {x: number; y: number} {
		const rect = this.canvas.getBoundingClientRect();
		return {x: clientX - rect.left, y: clientY - rect.top};
	}
}
